// Publish orchestration. Renders the site (lib/site-render.ts) and
// commits it to the public repo (lib/github-commit.ts) as one atomic
// snapshot. A "published post" is a draft with a slug, so the published
// set is just those drafts mapped to Post shapes.

import { type CreatorProfile, type Post, autoSummary } from "./post";
import { renderSite } from "./site-render";
import { commitFiles, type CommitResult } from "./github-commit";
import type { Draft } from "./storage/drafts";

/** The "hello, world" post the wizard publishes first
 *  (04_CREATOR_ONBOARDING.md → Step 9). */
export function starterPost(nowIso: string): Post {
  const body = `Hello, world.

This is the first post on my new site — and it lives on infrastructure I own.
No platform sits between me and whoever's reading this. The words are in my own
GitHub repo; the site is served from my own Cloudflare account. If I ever want to
leave, everything comes with me, because it was always mine.

More soon.`;
  return {
    slug: "hello-world",
    title: "Hello, world",
    summary: "The first post on my new site, on my own infrastructure.",
    body,
    tags: ["intro"],
    kinds: ["text"],
    publishedAt: nowIso,
    updatedAt: nowIso,
  };
}

/** Map a published draft (slug set) to a feed/site Post. */
export function draftToPost(d: Draft): Post {
  let tags: string[] = [];
  try { tags = JSON.parse(d.tags || "[]"); } catch { tags = []; }
  const publishedIso = new Date(d.published_at ?? d.updated_at).toISOString();
  return {
    slug: d.published_slug!,
    title: d.title.trim() || "Untitled",
    summary: d.summary.trim() || autoSummary(d.body),
    body: d.body,
    tags,
    kinds: ["text"],
    publishedAt: publishedIso,
    updatedAt: new Date(d.updated_at).toISOString(),
  };
}

/** Render `posts` into the full public-repo tree and commit it. */
export async function publishSite(
  token: string,
  publicRepo: string,
  profile: CreatorProfile,
  posts: Post[],
  message: string,
): Promise<CommitResult> {
  const nowIso = new Date().toISOString();
  const files = renderSite(profile, posts, nowIso);
  return commitFiles(token, publicRepo, files, message);
}
